import React, { createContext, useState, useMemo, useCallback, useContext } from 'react';
import { productService } from '../services/ProductsService';

export const ProductContext = createContext();

export const ProductProvider = props => {
    const [products] = useState(new Map());
    const [dropdownProductData, setDropdownProductData] = useState([]);
    const [err, setErr] = useState(null);
    const [productTypes] = useState([
        'SaaS Analytics',
        'Software Application',
        'Software Engineering',
        'Hardware'
    ]);
    const [productOccurrences] = useState([
        'Yearly',
        '3 Year',
        'One Time'
    ]);
    const [productUnits] = useState([
        'per/year',
        'each',
        'per/sq.ft.'
    ]);
    const [productsList, setProductsList] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortState, setSortState] = useState([{ fieldName: 'DistributorID', dir: 'asc' }]);

    const loadProducts = useCallback(() => {
        productService
            .getProducts()
            .then(res => {
                res.data.forEach(product => {
                    products.set(product.id, product);
                });
                const dropdownData = res.data.map(product => {
                    return { key: product.id, text: product.name, value: product.id, price: product.price, enabled: product.enabled.toString()};
                });
                setDropdownProductData(dropdownData);
            })
            .catch(err => setErr(err));
    }, [products]);

    const createProduct = useCallback((name, price, description, type, occurrence, units, componentId) => {
        return productService.postProduct(name, price, description, type, occurrence, units, componentId);
    }, []);

    const getProductById = useCallback(
        id => {
            return products.get(id);
        },
        [products]
    );

    const getProducts = useCallback(async () => {
        const result = await productService.getProducts(pageSize, pageIndex, sortState[0]?.fieldName, sortState[0]?.dir);
        setTotal(result.total);
        setProductsList(result.data);
    }, [pageSize, pageIndex, sortState]);

    const fetchProduct = useCallback(id => {
        return productService.getProduct(id);
    }, []);

    const editProduct = useCallback(async (id, name, price, description, type, occurrence, units, componentId) => {
        return await productService.editProduct(id, name, price, description, type, occurrence, units, componentId);
    }, []);

    const deleteProduct = useCallback(async id => {
        return await productService.deleteProduct(id);
    }, []);

    const toggleProduct = useCallback(async (distributorId, productId, enabled) => {
        return await productService.toggleProduct(distributorId, productId, enabled);
    }, []);

    const values = useMemo(
        () => ({
            products,
            productsList,
            dropdownProductData,
            err,
            productTypes,
            productOccurrences,
            productUnits,
            total,
            pageIndex,
            pageSize,
            sortState,
            getProducts,
            getProductById,
            fetchProduct,
            editProduct,
            deleteProduct,
            createProduct,
            loadProducts,
            toggleProduct,
            setPageIndex,
            setPageSize,
            setSortState
        }),
        [
            products,
            productsList,
            dropdownProductData,
            err,
            productTypes,
            productOccurrences,
            productUnits,
            total,
            pageIndex,
            pageSize,
            sortState,
            getProducts,
            getProductById,
            fetchProduct,
            editProduct,
            deleteProduct,
            createProduct,
            loadProducts,
            toggleProduct,
            setPageIndex,
            setPageSize,
            setSortState
        ]
    );

    return <ProductContext.Provider value={values}>{props.children}</ProductContext.Provider>;
};

export const useProducts = () => {
    const context = useContext(ProductContext);

    if (context === undefined) {
        throw new Error('useProducts must be used within a ProductProvider component.');
    }

    return context;
};
